import { useEffect, useRef, useState } from "react";
import {
  realtimeClient,
  type RealtimeEventPayload,
  type RealtimeStatus,
} from "@/lib/realtimeClient";

/**
 * Realtime-Hooks für die React-Welt.
 *
 * Alle Hooks teilen sich *eine* zentrale WebSocket-Verbindung
 * (siehe `realtimeClient`). Pro Hook-Instanz wird lediglich ein Listener
 * registriert, der auf die übergebenen Scopes/Domains/Events hört. Trifft ein
 * Event ein (oder das Fallback-Polling tickt), wird `onChange` aufgerufen.
 *
 * Wiederverbindung, Heartbeat und Reconnect-Backoff übernimmt der zentrale
 * Client. Bei einem kurzzeitigen Verbindungsabbruch verbindet er sich
 * automatisch neu.
 */
export interface RealtimeOptions {
  /** Scopes wie `auftraege:list` – siehe Backend-Routen. */
  scopes: string[];
  /** Domain-Filter – z. B. `auftraege`, `hero`, `projects`. */
  domains?: string[];
  /** Event-Filter – Strings wie `auftraege_changed`. */
  events?: string[];
  /** Polling-Intervall (ms) als Fallback. Default 180_000. */
  fallbackIntervalMs?: number;
  /** Wird aufgerufen, wenn ein passendes Event eingeht oder das Polling tickt. */
  onChange: (payload?: RealtimeEventPayload) => void;
  /** Wenn false, ist der Hook deaktiviert (z. B. solange `user` nicht geladen). */
  enabled?: boolean;
}

export function useRealtime(opts: RealtimeOptions): void {
  const { scopes, domains, events, fallbackIntervalMs = 180_000, onChange, enabled = true } = opts;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Stabile Schlüssel, damit wir nicht bei jedem Render neu registrieren.
  const scopeKey = (scopes || []).join(",");
  const domainKey = (domains || []).join(",");
  const eventKey = (events || []).join(",");

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    const unregister = realtimeClient.addListener({
      scopes: scopeKey ? scopeKey.split(",") : [],
      domains: domainKey ? domainKey.split(",") : [],
      events: eventKey ? eventKey.split(",") : [],
      fallbackIntervalMs,
      onChange: (payload) => onChangeRef.current(payload),
    });
    return unregister;
  }, [enabled, scopeKey, domainKey, eventKey, fallbackIntervalMs]);
}

/**
 * Liefert den aktuellen Status der zentralen WebSocket-Verbindung
 * (`connecting` | `open` | `closed`) – z. B. für eine kleine Status-Anzeige.
 */
export function useRealtimeStatus(): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>(() => realtimeClient.getStatus());
  useEffect(() => realtimeClient.onStatus(setStatus), []);
  return status;
}

// Spiegelt die Backend-Domain-Aliase (siehe `api/realtime_events.py`).
const DOMAIN_SCOPE_ALIASES: Record<string, string[]> = {
  auftraege: ["projects"],
  projects: ["auftraege"],
  material_anforderungen: ["material-anforderungen"],
  "material-anforderungen": ["material_anforderungen"],
};

export interface RealtimeSyncOptions {
  /**
   * Backend-Domains, auf die reagiert werden soll – z. B. `["wartungen"]`.
   * Daraus werden automatisch Scopes (`domain:x`, `x:list`) und Events
   * (`x_changed`) inkl. bekannter Aliase abgeleitet.
   */
  domains: string[];
  /** Stilles Nachladen – sollte die Daten im Hintergrund aktualisieren. */
  onChange: () => void;
  /** Wenn false, ist der Hook deaktiviert. */
  enabled?: boolean;
  /**
   * Solange `true`, werden eingehende Updates aufgeschoben (z. B. während der
   * Benutzer ein Formular bearbeitet). Sobald `paused` auf `false` wechselt und
   * zwischenzeitlich Events anlagen, wird genau einmal nachgeladen.
   */
  paused?: boolean;
  /** Mehrfach-Events kurz bündeln (ms). Default 300. */
  debounceMs?: number;
  /** Zusätzliche Scopes (z. B. `auftraege:123`). */
  extraScopes?: string[];
  /** Zusätzliche Event-Namen. */
  extraEvents?: string[];
  /** Fallback-Polling (ms). Default 180_000. */
  fallbackIntervalMs?: number;
}

/**
 * Komfort-Hook: bindet eine Liste/Detailansicht still an Realtime-Updates an.
 *
 * Leitet Scopes/Events aus `domains` ab, bündelt Events (Debounce) und schützt
 * laufende Formulareingaben über `paused`.
 */
export function useRealtimeSync(opts: RealtimeSyncOptions): void {
  const {
    domains,
    onChange,
    enabled = true,
    paused = false,
    debounceMs = 300,
    extraScopes = [],
    extraEvents = [],
    fallbackIntervalMs = 180_000,
  } = opts;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const pausedRef = useRef(paused);
  const pendingRef = useRef(false);
  const debounceTimerRef = useRef<number | null>(null);

  const allDomains = new Set<string>();
  const scopes = new Set<string>(extraScopes);
  const events = new Set<string>(extraEvents);
  for (const raw of domains) {
    const d = String(raw || "").trim().toLowerCase();
    if (!d) continue;
    const variants = [d, ...(DOMAIN_SCOPE_ALIASES[d] || [])];
    for (const v of variants) {
      allDomains.add(v);
      scopes.add(`domain:${v}`);
      scopes.add(`${v}:list`);
      events.add(`${v}_changed`);
    }
  }

  const domainKey = [...allDomains].sort().join(",");
  const scopeKey = [...scopes].sort().join(",");
  const eventKey = [...events].sort().join(",");

  // Beim Wechsel von paused=true → false einmalig nachholen.
  // Ein schon gestarteter Debounce darf das Formular nicht mitten in der
  // Bearbeitung nachladen: sonst landet die Liste von vor dem Speichern
  // nach dem Speichern wieder in der Oberfläche.
  useEffect(() => {
    pausedRef.current = paused;
    if (paused) {
      if (debounceTimerRef.current != null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
        pendingRef.current = true;
      }
      return;
    }
    if (pendingRef.current) {
      pendingRef.current = false;
      onChangeRef.current();
    }
  }, [paused]);

  useRealtime({
    enabled,
    scopes: scopeKey ? scopeKey.split(",") : [],
    domains: domainKey ? domainKey.split(",") : [],
    events: eventKey ? eventKey.split(",") : [],
    fallbackIntervalMs,
    onChange: () => {
      if (pausedRef.current) {
        pendingRef.current = true;
        return;
      }
      if (debounceTimerRef.current != null) window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null;
        if (pausedRef.current) {
          pendingRef.current = true;
          return;
        }
        onChangeRef.current();
      }, debounceMs);
    },
  });

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current != null) window.clearTimeout(debounceTimerRef.current);
    };
  }, []);
}

export type { RealtimeEventPayload, RealtimeStatus };
